import React, { ReactNode } from 'react';
import './FileUpload.scss';
import { Button, Icon, Typography, Loader, Input } from '@design-system/index';
import { ModifierLine, ModifierFull, PoubelleLine, Upload, Fichier, Delete } from '@icons';
import { CdnFile } from '@services/core/cdn/file/file';
import { useFileUpload } from './useFileUpload';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import { Table } from '@components/Table/Table/Table';
import { FlowFile } from 'flowjs';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { Formik } from 'formik';
import { useEditFileMutation } from '@services/core/cdn/file/file.endpoints';

type FileUploadProps = {
    caption?: string;
    subtitle?: ReactNode;
    variant?: 'light';
    isMultiple?: boolean;
    isImage?: boolean;
    position?: number;
    width?: string;
    table?: boolean;
    height?: string;
    files?: CdnFile[] | CdnFile;
    onSuccess?: (file: CdnFile[]) => void;
    onOneSuccess?: (file: CdnFile) => void;
    onOneDelete?: (file: CdnFile) => void;
    onOneEdit?: (file: CdnFile) => void;
    accept?: string;
};

export const FileUpload: React.FC<FileUploadProps> = ({
    caption,
    subtitle,
    variant,
    files,
    onSuccess,
    onOneSuccess,
    onOneDelete,
    isMultiple = false,
    isImage = true,
    width = '100%',
    height = '100%',
    table = false,
    position,
    accept = '*',
    onOneEdit
}) => {
    const {
        uploadedFiles,
        inProgressFiles,
        errorFiles,
        inputFlowRef,
        handleDrop,
        handleDragOver,
        handleFileSelect,
        removeFile,
        removeErrorFile,
        updateFile,
        documentColumnHelper
    } = useFileUpload(files, onSuccess, onOneSuccess, onOneDelete, onOneEdit, isMultiple, position);
    const MySwal = withReactContent(Swal);
    const [editFile] = useEditFileMutation();

    const handleEditFile = async (id: number, row: CdnFile) => {
        await MySwal.fire({
            title: "Édition d'un document",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: 'Modifier',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            heightAuto: false,
            html: (
                <Formik
                    initialValues={{
                        documentName: row.label
                    }}
                    onSubmit={async (values) => {
                        try {
                            const response = await editFile({
                                id: id,
                                documentName: values.documentName
                            }).unwrap();
                            updateFile(response.data as unknown as CdnFile);
                            if (onOneEdit) onOneEdit(response.data as unknown as CdnFile);
                            MySwal.close();
                        } catch (error) {
                            console.error("Erreur lors de l'édition:", error);
                        }
                    }}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <form id="form-edit-file" onSubmit={handleSubmit}>
                            <Input
                                type="text"
                                name="documentName"
                                label="Nom du document"
                                value={values.documentName || ''}
                                onChange={handleChange}
                            />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-edit-file') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const documentsColumns = [
        documentColumnHelper.accessor(
            (row: CdnFile | FlowFile) => {
                if ('label' in row) {
                    return (
                        <div className="file-upload-table-item">
                            <Icon icon={<Fichier />} size={20} color="secondary" />
                            <a href={row.viewLink} target="_blank" rel="noopener noreferrer">{row.label}</a>
                        </div>
                    );
                } else {
                    if ((row as FlowFile).error) {
                        //in errror
                        return (
                            <div className="file-upload-table-item">
                                <Icon icon={<Fichier />} size={20} color="error" />
                                {row.name}
                            </div>
                        );
                    } else {
                        //in progress
                        return (
                            <div className="file-upload-table-item">
                                <Loader verysmall />
                                {row.name}
                            </div>
                        );
                    }
                }
            },
            {
                id: 'filename',
                header: 'Nom',
                size: 5000,
                cell: (info) => <>{info.getValue()}</>
            }
        ),

        documentColumnHelper.accessor(
            (row: CdnFile | FlowFile) => {
                if ('label' in row) {
                    return <>{moment(row.createdAt).format('DD/MM/YYYY')}</>;
                } else {
                    if ((row as FlowFile).error) {
                        //in errror
                        return (
                            <Typography intrinsic="p" color="error" style={{ whiteSpace: 'nowrap' }}>
                                Une erreur est survenue
                            </Typography>
                        );
                    } else {
                        //in progress
                        return <>{moment().format('DD/MM/YYYY')}</>;
                    }
                }
            },
            {
                header: 'Date',
                cell: (info) => <>{info.getValue()}</>
            }
        ),

        documentColumnHelper.accessor(
            (row: CdnFile | FlowFile) => {
                if ('label' in row) {
                    return (
                        <div className="file-upload-table-item">
                            <Button
                                type="button"
                                variant="icon-hover"
                                color="primary-secondary"
                                icon={<Icon icon={<PoubelleLine />} size={18} />}
                                onClick={() => removeFile(row)}
                            ></Button>
                            <Button
                                type="button"
                                variant="icon-hover"
                                color="primary-secondary"
                                icon={<Icon icon={<Upload />} size={18} />}
                                onClick={() => window.open(row.viewLink, '_blank')}
                            ></Button>
                            <Button
                                type="button"
                                variant="icon-hover"
                                color="primary-secondary"
                                icon={<Icon icon={<ModifierFull />} size={18} />}
                                onClick={() => handleEditFile(row.id, row)}
                            ></Button>
                        </div>
                    );
                } else {
                    if ((row as FlowFile).error) {
                        return (
                            <>
                                <Button
                                    type="button"
                                    variant="icon-hover"
                                    color="primary-secondary"
                                    icon={<Icon icon={<PoubelleLine />} size={18} />}
                                    onClick={() => removeErrorFile(row)}
                                ></Button>
                            </>
                        );
                    }
                    return <></>;
                }
            },
            {
                header: 'Action',
                cell: (info) => <>{info.getValue()}</>
            }
        )
    ] as ColumnDef<CdnFile | FlowFile>[];

    return (
        <div className="file-upload-container">
            <div className={`file-upload`} style={{ width, height }} onDrop={handleDrop} onDragOver={handleDragOver}>
                {/* Caption */}
                {caption && (
                    <div className="file-upload-caption">
                        <Typography intrinsic="h3" variant="secondary" color="secondary">
                            {caption}
                        </Typography>
                    </div>
                )}

                <input
                    type="file"
                    id="file-input"
                    multiple={isMultiple}
                    accept={isImage ? 'image/*' : accept}
                    ref={inputFlowRef}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                />

                {/* Content */}
                {variant === 'light' ? (
                    <div className="file-upload-content">
                        <Button
                            variant="secondary"
                            size="medium-square"
                            type="button"
                            icon={<Icon icon={<Upload />} size={18} color="primary" />}
                            onClick={() => inputFlowRef.current?.click()}
                        />
                    </div>
                ) : (
                    <div className="file-upload-content">
                        <Icon icon={<Upload />} size={24} color="primary" />
                        <Typography intrinsic="h3" variant="secondary" color="secondary" align="center">
                            Glisser/déposer un fichier
                            <br />
                            ou
                        </Typography>
                        <Button variant="primary" type="button" size="big" onClick={() => inputFlowRef.current?.click()}>
                            Importer un fichier
                        </Button>
                        {subtitle && (
                            <Typography intrinsic="p" variant="p2" color="grey1" align="center">
                                {subtitle}
                            </Typography>
                        )}
                    </div>
                )}

                {!isMultiple && isImage && !table && (
                    <>
                        {inProgressFiles.length > 0 && (
                            <div className="file-upload-loading">
                                <Loader className="file-upload-loading-loader" />
                                <img className="file-upload-loading-image" src={URL.createObjectURL(inProgressFiles[0].file)} alt="file" />
                            </div>
                        )}

                        {errorFiles.length > 0 && (
                            <div className="file-upload-error">
                                <img className="file-upload-error-image" src={URL.createObjectURL(errorFiles[0].file)} alt="file" />
                                <div className="file-upload-error-content">
                                    <Typography intrinsic="h3" variant="secondary" color="white" align="center">
                                        Une erreur est survenue
                                    </Typography>
                                    {variant === 'light' ? (
                                        <>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="medium-square"
                                                icon={<Icon icon={<PoubelleLine />} size={18} color="white" />}
                                                onClick={() => removeErrorFile(errorFiles[0])}
                                            ></Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="big"
                                                icon={<Icon icon={<PoubelleLine />} size={18} color="white" />}
                                                onClick={() => removeErrorFile(errorFiles[0])}
                                            >
                                                Supprimer la photo
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {uploadedFiles.length > 0 && (
                            <div className="file-uploaded-content">
                                <img src={uploadedFiles[0].viewLink} alt="file" />
                                <div className="file-uploaded-content-overlay">
                                    {variant === 'light' ? (
                                        <>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="medium-square"
                                                icon={<Icon icon={<ModifierLine />} size={18} color="white" />}
                                                hoverIcon={<Icon icon={<ModifierFull />} size={18} color="white" />}
                                                onClick={() => inputFlowRef.current?.click()}
                                            ></Button>
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="medium-square"
                                                icon={<Icon icon={<PoubelleLine />} size={18} color="primary" />}
                                                onClick={() => removeFile(uploadedFiles[0])}
                                            ></Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="big"
                                                onClick={() => inputFlowRef.current?.click()}
                                            >
                                                Modifier la photo
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="big"
                                                onClick={() => removeFile(uploadedFiles[0])}
                                            >
                                                Supprimer la photo
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
            {(isMultiple || !isImage) && !table && (
                <div className="file-upload-list">
                    {uploadedFiles.map((file) => (
                        <div className="file-upload-list-item" key={file.id}>
                            <Icon icon={<Fichier />} size={20} color="secondary" />
                            <a href={file.viewLink} target="_blank" rel="noopener noreferrer">
                                <Typography intrinsic="p" variant="p2" color="grey1">
                                    {file.label}
                                </Typography>
                            </a>
                            <div className="file-upload-list-item-actions">
                                <Button
                                    type="button"
                                    variant="icon-hover"
                                    color="grey-secondary"
                                    icon={<Icon icon={<ModifierFull />} size={18} color="grey1" />}
                                    onClick={() => handleEditFile(file.id, file)}
                                ></Button>
                                <Button
                                    type="button"
                                    variant="icon-hover"
                                    color="grey-secondary"
                                    icon={<Icon icon={<Delete />} size={18} color="grey1" />}
                                    onClick={() => removeFile(file)}
                                ></Button>
                            </div>
                        </div>
                    ))}
                    {inProgressFiles.map((file) => (
                        <div className="file-upload-list-item" key={file.uniqueIdentifier}>
                            <Loader verysmall />
                            <Typography intrinsic="p" variant="p2" color="grey1">
                                {file.file.name}
                            </Typography>
                        </div>
                    ))}

                    {errorFiles.map((file) => (
                        <div className="file-upload-list-item file-upload-list-item-error" key={file.uniqueIdentifier}>
                            <Icon icon={<Fichier />} size={20} color="error" />
                            <Typography intrinsic="p" variant="p2" color="secondary">
                                {file.file.name}
                            </Typography>
                            <Button
                                type="button"
                                variant="icon-hover"
                                color="secondary"
                                icon={<Icon icon={<Delete />} size={18} color="grey1" />}
                                onClick={() => removeErrorFile(file)}
                            ></Button>
                        </div>
                    ))}
                </div>
            )}

            {table && (
                <div className="file-upload-table">
                    <Table data={[...errorFiles, ...inProgressFiles, ...uploadedFiles]} columns={documentsColumns} />
                </div>
            )}
        </div>
    );
};
